import {sendEvent} from '@github-ui/hydro-analytics'
import {useEffect, useRef} from 'react'

import {useSafeChatState} from '../../utils/CopilotChatContext'
import {useEntitlement} from './EntitlementContext'

export type QuotaBannerVariant = 'quota_exceeded' | 'quota_exceeded_empty_state' | 'free_chat_exceeded'
export type QuotaBannerSource = 'copilot-chat' | 'copilot-immersive-v1'

interface UseQuotaBannerImpressionOptions {
  variant: QuotaBannerVariant
  source: QuotaBannerSource
}

/**
 * Fired exactly once per banner mount with the entitlement-shape data needed
 * to detect bugs that suppress chat input. The bug fixed in
 * https://github.com/github/github-ui/pull/20407 produces a unique signature
 * in this payload —
 * `remaining_chat_present: false, quotas_keys_count: 0, has_reset_date: true` —
 * which is the actual detection signal.
 *
 * Reads `mode` from CopilotChatProvider state rather than requiring callers
 * to pass it, since banners render across both assistive and immersive surfaces.
 * Falls back to 'unknown' outside a provider (e.g. Storybook).
 *
 * Uses a ref to guard against React strict-mode double-mount in dev.
 */
export function useQuotaBannerImpression({variant, source}: UseQuotaBannerImpressionOptions): void {
  const {licenseType, plan, quotaTelemetry, tokenBasedBillingEnabled} = useEntitlement()
  const mode = useSafeChatState()?.mode ?? 'unknown'
  const firedRef = useRef(false)

  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    sendEvent('dotcom_chat.banner_shown', {
      variant,
      mode,
      source,
      licenseType: licenseType ?? 'unknown',
      plan: plan ?? 'unknown',
      remainingChatPresent: quotaTelemetry?.remainingChatPresent ?? false,
      remainingChatValue: quotaTelemetry?.remainingChatValue ?? 'absent',
      remainingPremiumPresent: quotaTelemetry?.remainingPremiumPresent ?? false,
      quotasKeysCount: quotaTelemetry?.quotasKeysCount ?? 0,
      hasResetDate: quotaTelemetry?.hasResetDate ?? false,
      tokenBasedBillingEnabled,
    })
    // We intentionally only fire on the first mount with the values present at
    // that moment — the entitlement state at impression time is what we care
    // about for detecting bugs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
